import { Typography } from '@mui/material'
import { FeatureFlagIdGrantReasons } from 'c1gen/features'
import { C1Button } from 'c1ui'
import { C1TableRedux, C1TableReduxColumn } from 'components/core/list/C1Table/C1TableRedux'
import List from 'components/core/list/index'
import { CreateCustomEntitlement } from 'components/pages/app/controls/CreateCustomEntitlement'
import { C1EntitlementDisplayName } from 'components/pages/entitlement/display-name/index'
import { GrantOrigin } from 'components/pages/entitlement/grant-origin/GrantOrigin'
import { appEntitlementId } from 'components/pages/entitlement/helpers'
import {
    AppEntitlementFilters,
    AppEntitlementSearchFilter,
} from 'components/pages/requestcatalog/details/setup/add_entitlements/list/AppEntitlementSearchFilter'
import { useDrawer } from 'hooks/useDrawer'
import { AppEntitlementValue } from 'lib/actions/apps/app-entitlement'
import { SystemAnyAccessEntitlementID } from 'lib/constants/system'
import {
    AppEntitlementSearchServiceSearchAncestorsRequest,
    AppEntitlementSearchServiceSearchRequest,
} from 'pbts/c1/api/app/v1/app_entitlement'
import { useCallback, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { appEntitlementsSearchAncestorsApi, appEntitlementsSearchApi } from 'redux_toolkit/entitlements'

const cols: Array<C1TableReduxColumn<AppEntitlementValue, string>> = [
    {
        field: 'entitlement',
        flex: 3,
        label: (
            <FormattedMessage
                id="components_catalog_entitlement_picker_table_entitlement"
                defaultMessage="Entitlement"
            />
        ),
        Component: ({ row: entitlement }: { row: AppEntitlementValue }): React.ReactElement => (
            <C1EntitlementDisplayName entitlement={entitlement} />
        ),
    },
    {
        field: 'description',
        flex: 3,
        label: (
            <FormattedMessage
                id="components_catalog_entitlement_picker_table_grant_desc"
                defaultMessage="Description"
            />
        ),
        Component: ({ row }) => <Typography>{row.description}</Typography>,
    },
    {
        field: 'grantCount',
        label: (
            <FormattedMessage
                id="components_catalog_entitlement_picker_table_grant_count"
                defaultMessage="Grant count"
            />
        ),
        Component: ({ row }) => <Typography>{row.grantCount}</Typography>,
    },
    {
        field: 'origins',
        shouldRender: (hasFeature) => hasFeature(FeatureFlagIdGrantReasons) || false,
        Component: ({ row: appEntitlementValue, extras }) => (
            extras && <GrantOrigin
                appId={appEntitlementValue.appId}
                appEntitlementId={appEntitlementValue.id}
                appUserId={extras}
            />
        ),
        maxWidth: '100px',
    },
]

const CreateCustomEntitlementButton = ({ appId }: { appId: string }): React.ReactElement => {
    const [showDrawer, closeDrawer, setCanClose] = useDrawer()

    const linkedEntsReq = AppEntitlementSearchServiceSearchAncestorsRequest.create({
        appId,
        appEntitlementId: SystemAnyAccessEntitlementID,
        depth: 2,
        expandMask: { paths: ['*'] },
        leavesOnly: true,
        systemCreatedProxyBindingsOnly: true,
    })
    const {
        data: configureLinkedEntitlementsData,
        isError: isConfigureLinkedEntitlementsError,
    } = appEntitlementsSearchAncestorsApi.useSearchAncestors(linkedEntsReq)

    const createCustomEnt = useCallback(
        (): void => showDrawer(<CreateCustomEntitlement
            appId={appId}
            linkedEnts={!isConfigureLinkedEntitlementsError ? configureLinkedEntitlementsData?.[0].map((e) => e.ancestorEntitlement) : []}
            close={closeDrawer}
            setCanClose={setCanClose}
        />),
        [appId, configureLinkedEntitlementsData, isConfigureLinkedEntitlementsError, showDrawer, closeDrawer, setCanClose],
    )

    return <C1Button variant="outlined" onClick={createCustomEnt}>
        <FormattedMessage id="components_entitlement_create_virtual" defaultMessage="Create virtual entitlement" />
    </C1Button>
}

type AppEntitlementsProps = {
    appId: string
    appUserId?: string
    resourceTraitIds?: Array<string>
    allowCreateCustomEntitlement?: boolean
    hiddenFilters?: AppEntitlementFilters[]
}

export const AppEntitlements = ({
    appId,
    appUserId,
    resourceTraitIds,
    hiddenFilters,
    allowCreateCustomEntitlement = false,
}: AppEntitlementsProps): React.ReactElement => {
    const [filter, setFilter] = useState<AppEntitlementSearchServiceSearchRequest>(
        AppEntitlementSearchServiceSearchRequest.create({
            expandMask: { paths: ['*'] },
            appIds: [appId],
            appUserIds: appUserId ? [appUserId] : undefined,
            resourceTraitIds,
        }),
    )

    const {
        data: allEntitlements,
        isLoading,
        isError,
        getPage,
        canLoadMore,
        error: listEntitlementsError,
    } = appEntitlementsSearchApi.useSearch(filter, { actionable: !!appId })

    const [page, setPage] = useState<number>(0)

    const onPageChange = useCallback(
        async (n: number) => {
            await getPage(n)
            setPage(n)
        },
        [getPage],
    )

    const onFilterChange = useCallback((req: AppEntitlementSearchServiceSearchRequest): void => {
        setFilter(req)
    }, [])

    const search = useMemo(
        () => <AppEntitlementSearchFilter
            onChange={onFilterChange}
            initialState={filter}
            hiddenFilters={['app', ...(hiddenFilters || [])]}
        />,
        [filter, hiddenFilters, onFilterChange],
    )

    const table = (
        <C1TableRedux<AppEntitlementValue, string>
            data={allEntitlements}
            cols={cols}
            canLoadMore={canLoadMore}
            isError={isError}
            isLoading={isLoading}
            onPageChange={onPageChange}
            error={listEntitlementsError}
            page={page}
            getId={appEntitlementId}
            staticExtraProps={appUserId}
        />
    )

    return <List<AppEntitlementValue>
        search={search}
        table={table}
        buttons={allowCreateCustomEntitlement ? <CreateCustomEntitlementButton appId={appId} /> : undefined}
    />
}
